/**
 * enterTrainingSandbox
 *
 * Canonical "go to the training sandbox" sequence shared by every tour
 * surface that offers sandbox entry (header chips, the tour index, chat
 * action chips, and runner recovery buttons):
 *
 *   1. POST /api/v1/demo/enter for the module's sandbox lease
 *   2. Prime the demo client-context override + drop cached permissions
 *   3. Stash a pending tour open so the tour resumes after the reload
 *   4. Hard-navigate to the sandbox target path
 *
 * The page always reloads — the sandbox is a different client context and
 * every data layer re-resolves against the demo client id.
 */

import { enterDemoSession, setOperationsDemoClientContext } from '@/lib/demo/clientContext';
import { permissionCache } from '@/lib/permissionCache';
import { stashPendingTourOpen } from '../GuidedTourProvider';

export interface EnterTrainingSandboxOptions {
  moduleKey: string;
  /** Optional tour to resume once the demo client mounts. */
  resume?: { flowId?: string; objectiveId?: string };
}

export async function enterTrainingSandbox(opts: EnterTrainingSandboxOptions): Promise<void> {
  const result = await enterDemoSession(opts.moduleKey);
  if (!result.success || !result.session) {
    throw new Error('Sandbox activation failed');
  }

  setOperationsDemoClientContext(result.session);
  permissionCache.clearCache();
  stashPendingTourOpen({ moduleKey: opts.moduleKey, ...opts.resume });

  const url = new URL(
    result.session.target_path || `/client-zone/modules/${opts.moduleKey}`,
    window.location.origin,
  );
  url.searchParams.set('training_refresh', String(Date.now()));
  window.location.replace(`${url.pathname}${url.search}${url.hash}`);
}
